import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { Link } from 'react-router-dom';

const SetupWizard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAdvancedEnv, setShowAdvancedEnv] = useState(false);

  const [setupToken, setSetupToken] = useState('');

  const [writeEnv, setWriteEnv] = useState(true);

  const [env, setEnv] = useState({
    DB_HOST: 'localhost',
    DB_PORT: '5432',
    DB_NAME: 'koniza_financial',
    DB_USER: 'postgres',
    DB_PASSWORD: '',

    JWT_SECRET: '',
    INTEGRATION_JWT_SECRET: '',

    EMAIL_HOST: 'smtp.gmail.com',
    EMAIL_PORT: '465',
    EMAIL_SECURE: 'true',
    EMAIL_USER: '',
    EMAIL_PASSWORD: '',
    EMAIL_FROM: '',

    CLIENT_URL: 'http://localhost:3000',
    SERVER_URL: 'http://localhost:5000',
    FRONTEND_ORIGIN: 'http://localhost:3000',

    LOGIN_NOTIFICATION_ENABLED: 'true',

    // Advanced / optional
    PORT: '5000',
    RATE_LIMIT_MAX: '200',
    SWAGGER_AT_ROOT: 'false',
    SYSTEM_DATE: '',
    SKIP_CLIENT_BUILD: 'false'
  });

  const [company, setCompany] = useState({
    companyName: '',
    address: '',
    phoneNumber: '',
    email: '',
    bank_name: '',
    branch_code: '',
    account_number: '',
    account_type: 'Cheque'
  });

  const canRun = useMemo(() => {
    if (!status) return false;
    const alreadySetup = !!status.company?.exists;
    return !!status.enabled && !status.production && !alreadySetup;
  }, [status]);

  const alreadySetup = useMemo(() => {
    return !!status?.company?.exists;
  }, [status]);

  const hasToken = useMemo(() => {
    try { return !!localStorage.getItem('token'); } catch { return false; }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(apiUrl('/api/setup/status'));
        setStatus(res.data);
        // If env file already exists, default to NOT overwriting it.
        if (res.data?.envFileExists) setWriteEnv(false);
      } catch (e) {
        setError(e.response?.data?.error || e.message || 'Failed to load setup status');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (alreadySetup) {
      setError('System is already set up. Use the main app settings instead.');
      return;
    }
    try {
      const res = await axios.post(
        apiUrl('/api/setup/apply'),
        { env, company, writeEnv },
        { headers: { 'X-Setup-Token': setupToken } }
      );
      setSuccess(res.data);
      // refresh status
      const st = await axios.get(apiUrl('/api/setup/status'));
      setStatus(st.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Setup failed');
    }
  };

  const setEnvField = (k, v) => setEnv(prev => ({ ...prev, [k]: v }));
  const setCompanyField = (k, v) => setCompany(prev => ({ ...prev, [k]: v }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-indigo-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 md:p-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">First-Time Setup</h1>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Configure company details and (optionally) generate a local server <code>.env</code>.
            </p>
          </div>
          <Link to="/login" className="text-sm text-indigo-700 dark:text-indigo-400 hover:underline">Back to login</Link>
        </div>

        {loading ? (
          <div className="mt-6 text-gray-700 dark:text-slate-300">Loading setup status…</div>
        ) : error ? (
          <div className="mt-6 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg">{error}</div>
        ) : null}

        {status && (
          <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl">
            <div className="text-sm text-gray-700 dark:text-slate-300">
              <div><span className="font-semibold">Setup enabled:</span> {String(status.enabled)}</div>
              <div><span className="font-semibold">Production mode:</span> {String(status.production)}</div>
              <div><span className="font-semibold">server/.env exists:</span> {String(status.envFileExists)}</div>
              <div className="mt-2"><span className="font-semibold">Company config complete:</span> {String(status.company?.complete)}</div>
              {Array.isArray(status.company?.missingFields) && status.company.missingFields.length > 0 && (
                <div className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                  Missing: {status.company.missingFields.join(', ')}
                </div>
              )}
            </div>
            {!canRun && (
              <div className="mt-3 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                {alreadySetup
                  ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-semibold">System already setup</div>
                        <div className="text-sm">Click below to go to the system.</div>
                      </div>
                      <Link
                        to={hasToken ? '/dashboard' : '/login'}
                        className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                      >
                        {hasToken ? 'Go to Dashboard' : 'Go to Login'}
                      </Link>
                    </div>
                  )
                  : (
                    <>Setup is locked. To enable it locally, set <code>SETUP_TOKEN</code> in the server environment and run from localhost.</>
                  )}
              </div>
            )}
            {status?.envFileExists && (
              <div className="mt-3 text-xs text-gray-600 dark:text-slate-400">
                Note: this wizard will not overwrite an existing <code>server/.env</code> unless the server env var <code>ALLOW_SETUP_ENV_OVERWRITE=true</code>.
              </div>
            )}
          </div>
        )}

        {alreadySetup ? (
          <div className="mt-6 p-4 border border-gray-200 dark:border-slate-600 rounded-xl">
            <div className="text-gray-900 dark:text-slate-100 font-semibold">Setup is already completed.</div>
            <div className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Use the normal in-app configuration pages (for example <code>/company-config</code>) to make changes.
            </div>
            <div className="mt-4">
              <Link
                to={hasToken ? '/dashboard' : '/login'}
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-700"
              >
                {hasToken ? 'Go to Dashboard' : 'Go to Login'}
              </Link>
            </div>
          </div>
        ) : (
        <form onSubmit={onSubmit} className="mt-6 space-y-6">
          <div className="p-4 border border-gray-200 dark:border-slate-600 rounded-xl">
            <h2 className="font-semibold text-gray-900 dark:text-slate-100">Setup Token</h2>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Required. This must match <code>SETUP_TOKEN</code> on the server.
            </p>
            <input
              value={setupToken}
              onChange={(e) => setSetupToken(e.target.value)}
              className={`mt-3 w-full ${setupToken.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
              placeholder="Paste setup token"
            />
          </div>

          <div className="p-4 border border-gray-200 dark:border-slate-600 rounded-xl">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold text-gray-900 dark:text-slate-100">Generate server/.env (local only)</h2>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                <input type="checkbox" checked={writeEnv} onChange={(e) => setWriteEnv(e.target.checked)} />
                Write .env
              </label>
            </div>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              This writes <code>server/.env</code> only for local development. You must restart the server afterwards.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {[
                ['DB_HOST','DB Host'],['DB_PORT','DB Port'],['DB_NAME','DB Name'],['DB_USER','DB User'],['DB_PASSWORD','DB Password'],
                ['JWT_SECRET','JWT Secret'],['INTEGRATION_JWT_SECRET','Integration JWT Secret'],
                ['EMAIL_HOST','Email Host'],['EMAIL_PORT','Email Port'],['EMAIL_SECURE','Email Secure'],['EMAIL_USER','Email User'],['EMAIL_PASSWORD','Email Password'],['EMAIL_FROM','Email From'],
                ['CLIENT_URL','Client URL'],['SERVER_URL','Server URL'],['FRONTEND_ORIGIN','Frontend Origin'],
                ['LOGIN_NOTIFICATION_ENABLED','Login Notification Enabled']
              ].map(([k,label]) => (
                <div key={k}>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">{label}</label>
                  <input
                    value={env[k] || ''}
                    onChange={(e) => setEnvField(k, e.target.value)}
                    className="w-full"
                    placeholder={k}
                    type={k.includes('PASSWORD') || k.includes('SECRET') ? 'password' : 'text'}
                  />
                </div>
              ))}
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowAdvancedEnv(v => !v)}
                className="text-sm text-indigo-700 dark:text-indigo-400 hover:underline"
              >
                {showAdvancedEnv ? 'Hide advanced env' : 'Show advanced env'}
              </button>

              {showAdvancedEnv && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {[
                    ['PORT','Server Port'],
                    ['RATE_LIMIT_MAX','Rate Limit Max'],
                    ['SWAGGER_AT_ROOT','Swagger At Root'],
                    ['SKIP_CLIENT_BUILD','Skip Client Build'],
                    ['SYSTEM_DATE','System Date Override (ISO)']
                  ].map(([k,label]) => (
                    <div key={k} className={k === 'SYSTEM_DATE' ? 'md:col-span-2' : ''}>
                      <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">{label}</label>
                      <input
                        value={env[k] || ''}
                        onChange={(e) => setEnvField(k, e.target.value)}
                        className="w-full"
                        placeholder={k}
                        type="text"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="p-4 border border-gray-200 dark:border-slate-600 rounded-xl">
            <h2 className="font-semibold text-gray-900 dark:text-slate-100">Company Details</h2>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Saved to the database (company_config). This is what the app uses for invoices, emails, and bank details.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {[
                ['companyName','Company Name'],['email','Company Email'],['phoneNumber','Phone Number'],['address','Address'],
                ['bank_name','Bank Name'],['branch_code','Branch Code'],['account_number','Account Number']
              ].map(([k,label]) => (
                <div key={k} className={k === 'address' ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">{label}</label>
                  <input
                    value={company[k] || ''}
                    onChange={(e) => setCompanyField(k, e.target.value)}
                    className="w-full"
                    placeholder={label}
                    type="text"
                  />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Account Type</label>
                <select
                  value={company.account_type}
                  onChange={(e) => setCompanyField('account_type', e.target.value)}
                  className="w-full"
                >
                  <option value="Cheque">Cheque</option>
                  <option value="Savings">Savings</option>
                  <option value="Transmission">Transmission</option>
                </select>
              </div>
            </div>
          </div>

          {success && (
            <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 rounded-lg">
              Setup applied successfully. {success.note ? <span className="font-semibold">{success.note}</span> : null}
              {success.envWritten && (
                <div className="mt-2 text-sm">
                  Server restart required for the new <code>.env</code> to load.
                </div>
              )}
            </div>
          )}

          <button
            disabled={!canRun}
            type="submit"
            className={`w-full py-3 rounded-xl font-semibold text-white ${canRun ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-400 dark:bg-slate-600 cursor-not-allowed'}`}
          >
            Apply Setup
          </button>

          <div className="text-xs text-gray-600 dark:text-slate-400">
            Security: setup is restricted to localhost + requires <code>X-Setup-Token</code>. Do not enable in production.
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default SetupWizard;
